import * as React from "react";
import { FormComponent } from "../../../react-core/Html/FormComponent";
import { PartLoading } from "../../../react-core/Html/PartLoading";
import { Pager } from "../../../react-core/Html/Pager";
import { ContentTable } from "../../Admin/Company/CompanyWizard/Resource/Content";
import { TableColSettingsAction } from "../TableColFilter/Action/Action";
import { defaultTableColEditor } from "../TableColFilter/Resource/Content";
import { TableColSettingsStore } from "../TableColFilter/Store/Variable";
import { ScrollTable } from "./ScrollTable";
import { TableRow } from "./Components/TableRow";
import { TableHeader } from "./Components/TableHeader";
import { CommonHelper } from "../../../react-core/Helpers/CommonHelper";
import * as classNames from "classNames";
import { checkPermission } from "../Permission/PermissionHelper";
import { UserClaim } from "../../../Wrap/Constants/Enums/UserClaim";

export interface ITableProps extends React.Props<Table> {
    cssWrapper?: string;
    onRef?: (ref) => void;
    noDefaultCssWrapper?: boolean;
    tHeadCssWrapper?: string;
    rowCssWrapper?: string;
    isLoading?: boolean;
    needPagination?: IPaginationCofig;
    isScrollable?: boolean;
    isExpanedCondition?: (row, index) => boolean;
    SubTitle?: string;
    SubBody?: ICombineTableConfig[];
    TotalBody?: ICombineTableConfig[];
    DisplayTotalText?: boolean;
    TotalTextcolSpan?:number;
    col: ITableConfig[];
    data: any[];
    colEditorKey?: string;
    showEmptyMsg?: boolean;
    cssEmptyMessage?: string;
    reorderIconTitle?: string;
    emptyMsg?: string;
    dragOption?: {
        dragable: boolean;
        onReorder: (result: any[]) => void;
        dragIconPosition?: "right" | "left";
        dragTitle?: string;
    };
}

export interface IPaginationCofig {
    totalNumOfResult: number;
    onPaginationChange?: (pageIndex, pageSize) => void;
    pageSize?: number; // default as 10
    currentPage: number;
    isShowPerPageOptions?: boolean;
}

export interface ICombineTableConfig {
    data: string | JSX.Element;
    colSpan?: number;
    colIndex?: number;
    tfTdCssWrapper?: string;
}

export interface ITableConfig {
    title?: string | JSX.Element;
    key?: string;
    dataIndex?: string;
    thCssWrapper?: string;
    tdCssWrapper?: string;
    tfTdCssWrapper?: string;
    thInnerCss?: string;
    colSpan?: number;
    filterTitle?: string;
    render?: (data, row, index) => JSX.Element | string;
    onClick?: (data, row, index) => any;
    sorter?: () => void;
    userClaim?: UserClaim | UserClaim[];
    sortDirection?: sortDirection;
    tableFilter?: any;
    filterOption?: { disableEdit?: boolean };
    toogleOptions?: (
        rowData,
        index
    ) => { expandContent?: any; isToogle?: boolean; onToogle?: (isToogle: boolean) => void; toogleable: boolean };
}

export enum sortDirection {
    ASC = "ASC",
    DESC = "DESC",
}

export interface ITableState {
    currentColSettings: any[];
    pageSize: number;
}

export class Table extends FormComponent<ITableProps, ITableState> {
    constructor(props: ITableProps) {
        super(props);
        this.state = {
            currentColSettings: null,
            pageSize: props.needPagination?.pageSize,
        };
        this.subscribeToStore(TableColSettingsStore, () => this.onTableColSettingsStoreChange());
    }

    componentDidMount() {
        this.getSettings();
        this.props.onRef ? this.props.onRef(this) : null;
    }

    private onTableColSettingsStoreChange() {
        this.getSettings();
    }

    private getSettings() {
        const { colEditorKey } = this.props;
        if (!colEditorKey) {return;}
        const _settings = JSON.parse(JSON.stringify(TableColSettingsStore.getData()));
        if (!_settings) {
            TableColSettingsAction.get(colEditorKey);
        } else if (JSON.stringify(_settings) == "{}") {
            TableColSettingsAction.post(defaultTableColEditor);
        } else if (!_settings[colEditorKey]) {
            TableColSettingsAction.post(defaultTableColEditor[colEditorKey], colEditorKey);
        } else {
            this.setState({ currentColSettings: _settings[colEditorKey] });
        }
    }

    private PAGESIZE = 10;

    private DRAGICONPOSITION = "right";

    private DRAGTITLE = "Reorder";

    private renderReorderIcon = () => {
        return (
            <button className="reorder-button" title={this.props.reorderIconTitle}>
                <span className="sr-only">Reorder</span>
                <span className="icon" aria-hidden="true"></span>
            </button>
        );
    };

    private getList = () => document.getElementById("drag-sort-table-draggable");

    private move = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();

        const list = e.currentTarget.parentNode,
            x = e.clientX,
            y = e.clientY;

        const swapItem = document.elementFromPoint(x, y) === null ? e.currentTarget : document.elementFromPoint(x, y);

        if (list === swapItem.parentNode) {
            const _swapItem = swapItem !== e.currentTarget.nextSibling ? swapItem : swapItem.nextSibling;
            list.insertBefore(e.currentTarget, _swapItem);
        }
        if (list.contains(swapItem) && list !== swapItem) {
            const dragItem = this.getDragItemByTarget(swapItem, list);
            const _swapItem = dragItem && dragItem !== e.currentTarget.nextSibling ? dragItem : swapItem.nextSibling;
            list.insertBefore(e.currentTarget, _swapItem);
        }
    };

    private getDragItemByTarget = (target: Element, pNode: Node) => {
        let _dragItem = null;
        pNode.childNodes.forEach((item) => {
            if (item.contains(target)) {
                _dragItem = item;
            }
        });
        return _dragItem;
    };

    private drop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        (e.currentTarget.childNodes[0] as HTMLElement).style.opacity = "1";
        const _list = this.getList();
        const result = [];
        _list.childNodes.forEach((element) => {
            const index: string = (element as HTMLElement).id;
            result.push(this.props.data[index]);
        });

        this.props.dragOption.onReorder(result);
    };

    private renderBody(data: any[], col: ITableConfig[]) {
        const { showEmptyMsg, emptyMsg, cssEmptyMessage, dragOption } = this.props;
        if (showEmptyMsg && data && data.length == 0) {
            return (
                <tbody>
                    <tr>
                        <td colSpan={col.length}>
                            {" "}
                            <div className={cssEmptyMessage}>{emptyMsg ? emptyMsg : ContentTable.noDataMsg}</div>
                        </td>
                    </tr>
                </tbody>
            );
        }

        if (!data || !col) {return null;}
        const rows = [];

        if (dragOption && dragOption.dragable) {
            const postion = dragOption.dragIconPosition || this.DRAGICONPOSITION;
            const reorderCol: ITableConfig = {
                title: dragOption.dragTitle || this.DRAGTITLE,
                key: "",
                render: () => {
                    return this.renderReorderIcon();
                },
            };
            if (postion == "left") {
                col = [reorderCol].concat(col);
            } else {
                col.push(reorderCol);
            }
        }
        data.forEach((ele, index) => {
            rows.push(
                <TableRow
                    data={ele}
                    col={col}
                    index={index}
                    key={`row-${index}`}
                    draggable={dragOption && dragOption.dragable}
                    rowCssWrapper={this.props.rowCssWrapper}
                    onMove={(e) => this.move(e)}
                    onDrop={(e) => this.drop(e)}
                />
            );
        });

        return (
            <tbody id={`drag-sort-table${dragOption ? "-draggable" : ""}`}>
                {this.props.SubTitle && (
                    <tr className="sub-head">
                        <td colSpan={col.length}>{this.props.SubTitle}</td>
                    </tr>
                )}
                {rows}
            </tbody>
        );
    }

    private renderSubBody(type: "sub" | "total", config: ICombineTableConfig[]) {
        if (!config) {return null;}
        const { currentColSettings } = this.state;
        let lastColSpan = 0;
        return (
            <tfoot>
                {
                    this.props.DisplayTotalText &&
                    <tr className="total">

                        <td
                            key={"Tot-RowText"}
                            colSpan={this.props.TotalTextcolSpan}>
                            {"Total"}
                        </td>
                    </tr>
                }
                <tr className={type == "sub" ? "sub-total" : "total"}>
                    {config.map((ele, index) => {
                        let disabledColumns = 0;
                        let colSpan = ele.colSpan ? ele.colSpan : 1;
                        if (currentColSettings != null && ele.colIndex != null && ele.colIndex > 0) {
                            if (
                                ele.colIndex >= currentColSettings.length ||
                                currentColSettings[ele.colIndex].Enabled == false
                            ) {
                                //if this column is removed, don't render this total
                                return null;
                            }
                            const columnsBefore = currentColSettings.slice(0, ele.colIndex);
                            disabledColumns = columnsBefore.filter((ele) => ele.Enabled == false).length;
                            colSpan = ele.colIndex - disabledColumns - lastColSpan;
                            lastColSpan = ele.colIndex - disabledColumns;
                        }

                        return (
                            <td
                                key={`sub-${index}`}
                                colSpan={colSpan}
                                className={index == 0 ? null : ele.tfTdCssWrapper}
                            >
                                {ele.data}
                            </td>
                        );
                    })}
                </tr>
            </tfoot>
        );
    }

    private renderPartLoading() {
        return <PartLoading isLoading={true} />;
    }

    private renderPagination() {
        const { totalNumOfResult, onPaginationChange, currentPage, isShowPerPageOptions } = this.props.needPagination;
        const { pageSize } = this.state;
        const _pageSize = pageSize || this.PAGESIZE;
        const _isShowPerPageOptions = CommonHelper.isNullOrEmpty(isShowPerPageOptions) ? false : isShowPerPageOptions;
        return (
            <Pager
                pager={{
                    CurrentPage: currentPage,
                    ResultsPerPage: _pageSize,
                    TotalPages: Math.ceil(totalNumOfResult / _pageSize),
                    TotalRecords: totalNumOfResult,
                }}
                onChange={(recordsPerPage: number, pageIndex: number) => {
                    this.setState({ pageSize: recordsPerPage });
                    onPaginationChange(pageIndex, recordsPerPage);
                }}
                recordsPerPageOptions={[20, 50, 100]}
                isShowPerPageOptions={_isShowPerPageOptions}
            />
        );
    }

    private getColumn(settings: any[], col: ITableConfig[]) {
        const result = [];
        settings.forEach((ele) => {
            if (ele.Enabled) {
                const findObj = col.find((item) => item.tableFilter == ele.Column);
                if (findObj) {result.push(col.find((item) => item.tableFilter == ele.Column));}
            }
        });
        return result;
    }

    private renderTContent(data, col) {
        return (
            <>
                <TableHeader col={col} cssWrapper={this.props.tHeadCssWrapper} />
                {this.renderBody(data, col)}
                {this.renderSubBody("sub", this.props.SubBody)}
                {this.renderSubBody("total", this.props.TotalBody)}
            </>
        );
    }

    public render(): JSX.Element {
        const { data, col, needPagination, isLoading, colEditorKey, isScrollable, noDefaultCssWrapper } = this.props;
        const { currentColSettings } = this.state;
        const _isSrollable = isScrollable != undefined ? isScrollable : true;
        if (isLoading == true || (colEditorKey && !currentColSettings)) {return this.renderPartLoading();}
        let _col = col.filter((ele) => ele !== null);
        _col = _col.filter((ele) => (ele.userClaim ? checkPermission(ele.userClaim) : true));
        _col = colEditorKey ? this.getColumn(currentColSettings, _col) : _col;
        const isRenderPagination = data && needPagination && needPagination.totalNumOfResult > 0;
        const cssWrapper = noDefaultCssWrapper
            ? this.props.cssWrapper
            : classNames("data-table", this.props.cssWrapper);
        return (
            <>
                {_isSrollable ? (
                    <ScrollTable cssWrapper={this.props.cssWrapper}>{this.renderTContent(data, _col)}</ScrollTable>
                ) : (
                    <table className={cssWrapper}>{this.renderTContent(data, _col)}</table>
                )}
                {isRenderPagination && this.renderPagination()}
            </>
        );
    }
}
